/**
 * Sidebar / menu structure consumed by AppMenu.vue.
 *
 * Visibility est gouvernée par DEUX axes principaux :
 *   - `feature_flag` : clé registry (`module.*` ou `feature.*`) résolue
 *      via la cascade enterprise → station → default. C'est la SOURCE
 *      DE VÉRITÉ pour l'activation/désactivation des modules.
 *      Une clé vide ("" ou absente) signifie « pas de gate » → toujours
 *      visible.
 *   - `allowed_resources` : permissions DB nécessaires. C'est l'unique
 *      source de vérité pour les droits d'accès — un Admin entreprise
 *      peut accorder n'importe quelle permission à n'importe quel rôle
 *      via `workspace-administration-roles`.
 *
 * Le champ `user_roles` n'est plus utilisé que pour les routes
 * strictement Root-only (concept SaaS, pas entreprise). Cf. la matrice
 * métier seedée par IdempotentRolePermissionMatrixSeeder pour les
 * permissions par défaut par rôle.
 *
 * IMPORTANT — chaque `feature_flag` ici DOIT correspondre au `meta.feature`
 * de la route cible dans router/index.ts. Sans cet alignement, le menu et
 * le router peuvent diverger (menu visible mais route bloquée, ou
 * inverse).
 */

export default function getMenu() {
	return [
		// ─────────── Dashboards ───────────
		{
			name: "dashboard",
			route: "workspace-dashboard",
			allowed_resources: ["all"],
			feature_flag: "feature.dashboards.main",
			title: { en: "Dashboard", fr: "Tableau de bord" },
			elements: [],
			icon: { active: "/media/icons/dashboard-icon.svg" },
		},
		{
			name: "investor-dashboard",
			route: "workspace-investor-dashboard",
			allowed_resources: ["investor-dashboard"],
			feature_flag: "feature.dashboards.investor",
			title: { en: "Investor Dashboard", fr: "Dashboard Investisseur" },
			elements: [],
			icon: { active: "/media/icons/dashboard-icon.svg" },
		},
		{
			name: "tax-dashboard",
			route: "workspace-tax-dashboard",
			allowed_resources: ["tax-dashboard"],
			feature_flag: "feature.dashboards.tax",
			title: { en: "Tax Dashboard", fr: "Dashboard Fiscal" },
			elements: [],
			icon: { active: "/media/icons/report-icon.svg" },
		},
		{
			name: "kpi-dashboard",
			route: "workspace-kpi-dashboard",
			allowed_resources: ["kpi"],
			feature_flag: "feature.dashboards.kpi",
			title: { en: "KPI Dashboard", fr: "KPI Opérationnels" },
			elements: [],
			icon: { active: "/media/icons/report-icon.svg" },
		},

		// ─────────── Agencies ───────────
		{
			name: "agency",
			allowed_resources: ["station", "retrieve-list-stations-only"],
			route: "workspace-agency-overview",
			feature_flag: "module.agencies",
			title: { en: "Agency", fr: "Agences" },
			elements: [],
			icon: { active: "/media/icons/agency-icon.svg" },
		},

		// ─────────── Operations ───────────
		{
			name: "operations",
			allowed_resources: [
				"passenger",
				"passenger-baggage",
				"package",
				"package-pickup",
				"package-transfer",
				"expense",
				"refund",
			],
			route: "workspace-operations-overview",
			feature_flag: "module.operations",
			title: { en: "Operations", fr: "Opérations" },
			elements: [
				{
					route: "workspace-operations-new-passenger",
					allowed_resources: ["passenger"],
					feature_flag: "feature.operations.passengers",
					title: { en: "New Passengers", fr: "Enreg. Passagers" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-operations-passenger-baggage",
					allowed_resources: ["passenger-baggage"],
					feature_flag: "feature.operations.baggages",
					title: { en: "Baggage Tickets", fr: "Etiquettes Bagages" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-operations-package-registration",
					allowed_resources: ["package"],
					feature_flag: "feature.operations.packages",
					title: { en: "Package Registration", fr: "Enregistrement Colis" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-operations-package-pickup",
					allowed_resources: ["package-pickup"],
					feature_flag: "feature.operations.package_pickup",
					title: { en: "Package Pickup", fr: "Retrait de Colis" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-operations-package-control",
					allowed_resources: ["package"],
					feature_flag: "feature.operations.package_control",
					title: { en: "Package Control", fr: "Controle des Colis" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-operations-package-loading",
					allowed_resources: ["package"],
					feature_flag: "feature.operations.qrcode_scan",
					title: { en: "Loading / Transfer", fr: "Chargement / Transfert" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-operations-package-delivery",
					allowed_resources: ["package-pickup"],
					feature_flag: "feature.operations.qrcode_scan",
					title: { en: "Home Delivery", fr: "Livraison à domicile" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-operations-package-tracking",
					allowed_resources: ["package"],
					feature_flag: "feature.operations.packages",
					title: { en: "Package Tracking", fr: "Suivi des Colis" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-operations-expense-management",
					allowed_resources: ["expense"],
					feature_flag: "feature.operations.expenses",
					title: { en: "Expenses Record", fr: "Enreg. Dépenses" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				// Caissier individuel : ouvrir/clôturer sa propre caisse.
				// PAS de feature_flag — la session de caisse est une feature
				// CORE (sécurité fonds) : sans elle, le middleware
				// cashier.session refuse 409 toute vente. Le menu doit
				// donc toujours être accessible aux rôles qui vendent,
				// indépendamment de l'activation SaaS d'autres modules.
				{
					route: "workspace-my-cashier-session",
					allowed_resources: ["cashier-session"],
					title: { en: "My Cash Drawer", fr: "Ma Caisse" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				// Audit des sessions de l'agence/entreprise (MainCashier+).
				// Pas de feature_flag non plus — l'audit reste critique
				// dès qu'il y a des sessions à valider.
				{
					route: "workspace-cashier-sessions-management",
					allowed_resources: ["cashier-session"],
					title: { en: "Cash Sessions Audit", fr: "Sessions de caisse" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-operations-reservations",
					allowed_resources: ["reservation"],
					feature_flag: "feature.operations.reservations",
					title: { en: "Reservations", fr: "Réservations" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-operations-refunds",
					allowed_resources: ["refund", "ticket"],
					feature_flag: "feature.operations.refunds",
					title: { en: "Refunds", fr: "Remboursements" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-operations-fleet-tracking",
					allowed_resources: ["vehicle"],
					feature_flag: "feature.tracking.fleet_live",
					title: { en: "Fleet Tracking", fr: "Suivi Flotte" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					// PR-9 : page contrôle d'embarquement + scan QR.
					route: "workspace-operations-boarding-control",
					allowed_resources: ["passenger"],
					feature_flag: "feature.operations.qrcode_scan",
					title: { en: "Boarding Control", fr: "Contrôle Embarq." },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
			],
			icon: { active: "/media/icons/operation-icon.svg" },
		},

		// ─────────── Auto Park ───────────
		{
			name: "autopark",
			allowed_resources: [
				"expedition",
				"vehicle",
				"driver",
				"technical-visit",
				"maintenance",
				"vehicle-tax",
				"vehicle-sale",
				"vehicle-rental",
				"expiration-alert",
			],
			route: "workspace-autopark-overview",
			feature_flag: "module.autopark",
			title: { en: "Auto Park", fr: "Parc Automobile" },
			elements: [
				{
					route: "workspace-autopark-vehicle",
					allowed_resources: ["vehicle"],
					feature_flag: "feature.autopark.vehicles",
					title: { en: "Vehicles", fr: "Véhicules" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-autopark-technical-visit",
					allowed_resources: ["technical-visit"],
					feature_flag: "feature.autopark.technical_visits",
					title: { en: "Technical Visit", fr: "Visites Techniques" },
					second_title: { en: "Technical Visit", fr: "Visites Technique" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-autopark-maintenance",
					allowed_resources: ["maintenance"],
					feature_flag: "feature.autopark.maintenance",
					title: { en: "Maintenances", fr: "Entretiens" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-autopark-insurance",
					allowed_resources: ["insurance"],
					feature_flag: "feature.autopark.insurance",
					title: { en: "Insurance", fr: "Assurances" },
					second_title: { en: "Insurance", fr: "Assurances" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-autopark-vehicle-tax",
					allowed_resources: ["vehicle-tax"],
					feature_flag: "feature.autopark.vehicle_tax",
					title: { en: "Vehicle Taxes", fr: "Taxes Véhicules" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-autopark-vehicle-sale",
					allowed_resources: ["vehicle-sale"],
					feature_flag: "feature.autopark.vehicle_sales",
					title: { en: "Vehicle Sales", fr: "Cessions Véhicules" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-autopark-vehicle-rental",
					allowed_resources: ["vehicle-rental"],
					feature_flag: "feature.autopark.vehicle_rentals",
					title: { en: "Vehicle Rentals", fr: "Locations Véhicules" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-autopark-expiration-alerts",
					allowed_resources: ["vehicle", "expiration-alert"],
					feature_flag: "feature.autopark.expiration_alerts",
					title: { en: "Expiration Alerts", fr: "Alertes d'Expiration" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
			],
			icon: { active: "/media/icons/autopark-icon.svg" },
		},

		// ─────────── Human Resources ───────────
		{
			name: "human-resources",
			allowed_resources: ["station-staff", "salary", "leave", "bonus"],
			route: "workspace-human-resources-overview",
			feature_flag: "module.human_resources",
			title: { en: "Human Resources", fr: "Ressources Humaines" },
			elements: [
				{
					route: "workspace-human-resources-staff",
					allowed_resources: ["station-staff"],
					feature_flag: "feature.hr.staff",
					title: { en: "Staff", fr: "Personnel" },
					second_title: { en: "Staff", fr: "Personnel" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-human-resources-salary",
					allowed_resources: ["salary"],
					feature_flag: "feature.hr.salary_payroll",
					title: { en: "Salary", fr: "Paiement Salaires" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-human-resources-leave",
					allowed_resources: ["leave"],
					feature_flag: "feature.hr.leave_management",
					title: { en: "Leave Management", fr: "Gestion des Congés" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-human-resources-bonus",
					allowed_resources: ["bonus"],
					feature_flag: "feature.hr.bonus_management",
					title: { en: "Bonus Management", fr: "Gestion des Primes" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
			],
			icon: { active: "/media/icons/human-resources-icon.svg" },
		},

		// ─────────── Planning ───────────
		{
			name: "planning",
			allowed_resources: [
				"expedition",
				"embarkation",
				"travel-route",
				"ticket-rate",
				"manifest",
				"package-transfer",
			],
			route: "workspace-planning-overview",
			feature_flag: "module.planning",
			title: { en: "Planning", fr: "Planifications" },
			elements: [
				{
					route: "workspace-planning-embarkation",
					allowed_resources: ["embarkation"],
					feature_flag: "feature.planning.embarkations",
					title: { en: "Embarkations", fr: "Embarquements" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-planning-expedition",
					allowed_resources: ["expedition"],
					feature_flag: "feature.planning.expeditions",
					title: { en: "Expeditions", fr: "Expéditions" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-planning-routes-and-rates",
					allowed_resources: ["travel-route", "ticket-rate"],
					feature_flag: "feature.planning.routes_rates",
					title: { en: "Routes and Rates", fr: "Trajets et Tarifs" },
					second_title: { en: "routes and rates", fr: "Trajets et tarifs" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-planning-others",
					allowed_resources: ["contract-type", "responsability-function", "payment-method"],
					feature_flag: "feature.planning.others_config",
					title: { en: "Others", fr: "Autres" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				// Travel Manifest — disabled pending modernization of the planning flow
				/*
				{
					route: "workspace-planning-manifest",
					allowed_resources: ["embarkation", "manifest"],
			feature_flag: "feature.planning.travel_manifest",
					title: { en: "Travel Manifest", fr: "Manifeste de Voyage" },
			second_title: { en: "", fr: "" },
			icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				*/
				{
					route: "workspace-planning-bordereau",
					allowed_resources: ["package-transfer", "expedition"],
					feature_flag: "feature.planning.transfer_bordereau",
					title: { en: "Transfer Slip", fr: "Bordereau de transfert" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
			],
			icon: { active: "/media/icons/planification-icon.svg" },
		},

		// ─────────── Providers ───────────
		{
			name: "providers",
			allowed_resources: ["providers"],
			route: "workspace-providers-overview",
			feature_flag: "module.providers",
			title: { en: "Providers", fr: "Fournisseurs" },
			elements: [],
			icon: { active: "/media/icons/provider-icon.svg" },
		},

		// ─────────── Reports ───────────
		{
			name: "reports",
			allowed_resources: ["travel-docket", "route-docket", "cash-register", "reconciliation"],
			route: "workspace-report-overview",
			feature_flag: "module.reports",
			title: { en: "Reports", fr: "Rapports" },
			elements: [
				{
					route: "workspace-report-travel-dockets",
					allowed_resources: ["travel-docket"],
					feature_flag: "feature.reports.travel_docket",
					title: { en: "Travel Dockets", fr: "Bordereaux de voyage" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-report-shipping-dockets",
					allowed_resources: ["route-docket"],
					feature_flag: "feature.reports.shipping_docket",
					title: { en: "Shipping Dockets", fr: "Bordereaux d'expédition" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-report-route-dockets",
					allowed_resources: [],
					feature_flag: "feature.reports.route_docket",
					title: { en: "Route Dockets", fr: "Bordereaux de route" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-report-receipts-report",
					allowed_resources: ["transaction-report"],
					feature_flag: "feature.accounting.receipts_report",
					title: { en: "Receipts Report", fr: "Rapport des Recettes" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-report-expenses-report",
					allowed_resources: ["transaction-report"],
					feature_flag: "feature.accounting.expenses_report",
					title: { en: "Expenses Report", fr: "Rapport des Dépenses" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-report-cash-register",
					allowed_resources: ["transaction-report", "cash-register"],
					feature_flag: "feature.accounting.cash_register",
					title: { en: "Cash Register", fr: "Rapport de Caisse (X/Z)" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-report-reconciliation",
					allowed_resources: ["transaction-report", "reconciliation"],
					feature_flag: "feature.accounting.reconciliation",
					title: { en: "Reconciliation", fr: "Réconciliation" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-report-bank-deposits",
					allowed_resources: ["transaction-report"],
					feature_flag: "feature.accounting.bank_deposits",
					title: { en: "Bank Deposits", fr: "Versements en Banque" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-report-alerts",
					allowed_resources: ["transaction-report"],
					title: { en: "Alerts", fr: "Alertes & Surveillance" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-report-scheduled",
					allowed_resources: ["transaction-report"],
					feature_flag: "feature.reports.scheduled_reports",
					title: { en: "Scheduled Reports", fr: "Rapports planifiés" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				// Déplacées depuis Administration : ce sont des rapports
				// opérationnels (passagers transférés, rabais accordés),
				// pas des actions administratives. URL et controllers
				// inchangés — seul le placement dans la sidebar bouge.
				{
					route: "workspace-administration-passengers-transferred",
					allowed_resources: ["transaction-report"],
					feature_flag: "feature.operations.passengers",
					title: { en: "Passenger Transferred", fr: "Passagers Transférés" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-administration-discounts-granted",
					allowed_resources: ["transaction-report"],
					feature_flag: "feature.accounting.transaction_history",
					title: { en: "Discounts Granted", fr: "Rabais Accordés" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
			],
			icon: { active: "/media/icons/report-icon.svg" },
		},

		// ─────────── Administration ───────────
		// Bloc réservé aux actions strictement administratives (gestion
		// utilisateurs/rôles/permissions, contrats, features, tarifs SaaS).
		// `transaction-report` a été retiré du parent — c'est une ressource
		// partagée avec les Reports financiers et son inclusion ici
		// rendait Administration visible à tout cashier ayant accès aux
		// recettes/dépenses. Les deux sub-views "Passengers Transferred"
		// et "Discounts Granted" ont été déplacées dans Reports
		// (cf. block reports ci-dessus) — elles sont des rapports, pas
		// des actions admin. URLs inchangées.
		{
			name: "administration",
			allowed_resources: ["permission", "enterprise", "region", "role"],
			route: "workspace-administration-overview",
			feature_flag: "module.administration",
			title: { en: "Administration", fr: "Administration" },
			elements: [
				{
					route: "workspace-administration-users",
					allowed_resources: ["user"],
					feature_flag: "feature.admin.users",
					title: { en: "Users Management", fr: "Gestion Utilisateurs" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-administration-permissions",
					allowed_resources: ["permission"],
					feature_flag: "feature.admin.permissions",
					title: { en: "Permissions Management", fr: "Gestion Permissions" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-administration-roles",
					allowed_resources: ["role"],
					feature_flag: "feature.admin.roles",
					title: { en: "Roles Management", fr: "Gestion Roles" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-administration-vehicle-types",
					allowed_resources: ["vehicle-type"],
					feature_flag: "feature.admin.vehicle_types",
					title: { en: "Vehicle Types Management", fr: "Gestion Types Vehicules" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-administration-feature-definitions",
					allowed_resources: ["enterprise"],
					user_roles: ["Root"],
					feature_flag: "feature.admin.feature_management",
					title: { en: "Features Registry", fr: "Registre des Features (Root)" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					// Configuration des features niveau SaaS : décide quelles
					// features sont disponibles à toute l'entreprise. C'est
					// une décision contractuelle/commerciale → Root only.
					// L'Admin entreprise ne configure PAS le catalogue, il
					// active/désactive des modules déjà autorisés via
					// « Gestion des Modules » (workspace-administration-features
					// gardé séparément).
					route: "workspace-administration-feature-flags",
					allowed_resources: ["enterprise"],
					user_roles: ["Root"],
					feature_flag: "feature.admin.feature_management",
					title: { en: "Feature Flags", fr: "Configuration Features" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-administration-pricing-catalog",
					allowed_resources: ["enterprise"],
					user_roles: ["Root"],
					feature_flag: "feature.admin.pricing_catalog",
					title: { en: "Pricing Catalog", fr: "Catalogue Tarifaire (Root)" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-administration-contracts",
					allowed_resources: ["enterprise"],
					user_roles: ["Root"],
					feature_flag: "feature.admin.subscriptions",
					title: { en: "Contracts", fr: "Liste Contrats (Root)" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-administration-trial",
					allowed_resources: ["enterprise"],
					user_roles: ["Root"],
					feature_flag: "feature.admin.subscriptions",
					title: { en: "Open Trial", fr: "Période d'essai (Root)" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					// "Mon Abonnement" est volontairement SANS feature_flag —
					// page de gestion contractuelle toujours accessible aux Admins
					// d'une entreprise (même si tous les modules sont coupés).
					route: "workspace-settings-my-subscription",
					allowed_resources: ["enterprise"],
					title: { en: "My Subscription", fr: "Mon Abonnement" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					// CRUD entreprises (création/suppression de tenants) =
					// administration SaaS, Root only. L'Admin entreprise
					// peut éditer ses propres infos via Mon Abonnement
					// + Settings (pas par ce menu).
					route: "workspace-administration-enterprise",
					allowed_resources: ["enterprise"],
					user_roles: ["Root"],
					feature_flag: "feature.admin.enterprises",
					title: { en: "Enterprise Management", fr: "Gestion Entreprise" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-administration-template",
					allowed_resources: ["templates"],
					feature_flag: "feature.admin.templates",
					title: { en: "Template Management", fr: "Gestion des Templates" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					route: "workspace-administration-regions",
					allowed_resources: ["region"],
					feature_flag: "feature.admin.regions",
					title: { en: "Regions Management", fr: "Gestion des Régions" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
				{
					// Gestion globale des modules / catalogue de features —
					// niveau SaaS, Root only. Cf. workspace-settings-my-subscription
					// (Mon Abonnement) pour l'Admin entreprise qui pilote son
					// propre périmètre via les overrides station/enterprise.
					route: "workspace-administration-features",
					allowed_resources: ["enterprise"],
					user_roles: ["Root"],
					feature_flag: "feature.admin.feature_management",
					title: { en: "Feature Management", fr: "Gestion des Modules" },
					second_title: { en: "", fr: "" },
					icon: { active: "/media/icons/....svg", inactive: "/media/icons/....svg" },
				},
			],
			icon: { active: "/media/icons/settings-icon.svg" },
		},

		// ─────────── Files ───────────
		{
			name: "files",
			allowed_resources: ["file"],
			route: "workspace-files-overview",
			feature_flag: "module.files",
			title: { en: "Files", fr: "Fichiers" },
			elements: [],
			icon: { active: "/media/icons/files-icon.svg" },
		},

		// ─────────── Logout (always visible) ───────────
		{
			name: "logout",
			allowed_resources: ["user-profile"],
			route: "auth-logout",
			title: { en: "Log Out", fr: "Déconnexion" },
			elements: [],
			icon: { active: "/media/icons/logout-icon.svg" },
		},

		// ─────────── Settings ───────────
		{
			name: "settings",
			allowed_resources: ["settings"],
			route: "workspace-settings-overview",
			feature_flag: "module.settings",
			title: { en: "Settings", fr: "Paramètres" },
			elements: [],
			icon: { active: "/media/icons/settings-icon.svg" },
		},

		// ─────────── Profile ───────────
		{
			name: "profile",
			allowed_resources: ["user-profile"],
			route: "workspace-profile-overview",
			feature_flag: "module.profile",
			title: { en: "Profile", fr: "Profil" },
			elements: [],
			icon: { active: "/media/icons/settings-icon.svg" },
		},
	];
}
